import PropTypes from 'prop-types';

import { auth } from '@/api/login';
import { encryptData } from '@/components/reusable/crypto/Cipher';
import { encodeBase64 } from '@/utils/common';
import { showToast } from '@/utils/toastMessage/ToastMessage';
import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { getDeviceFingerprint } from '@/utils/getDeviceFingerprint';
import { addFinancialData } from '@/redux/slices/FinancialYearDataSlice';
import { useDispatch } from 'react-redux';

const AuthContext = createContext();

/**
 * Custom hook to use the AuthContext.
 *
 * @returns {Object} The context value, which includes user data and authentication functions.
 */
export const userDataContext = () => {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error('AuthContext must be within the AuthContext Provider');
  }

  return context;
};

/**
 * Provider component that wraps its children in AuthContext.
 *
 * @param {Object} props - The props for the provider.
 * @param {React.ReactNode} props.children - The child components that will have access to the AuthContext.
 *
 * @returns {React.ReactElement} The provider component wrapping its children in AuthContext.
 */
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [successLoading, setSuccessLoading] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [username, setUsername] = useState();

  /**
   * Logs in the user by setting the user data.
   *
   * @param {Object} userData - The user data to set.
   */
  const login = (userData) => {
    setUser(userData);
  };

  const detailsForSingleBranchAndCompany = (userData) => {
    Cookies.set('companyName', userData?.companyName, {
      expires: 7,
    });
    Cookies.set('brandName', userData?.branchName, {
      expires: 7,
    });
    Cookies.set('brandAddress', userData?.branchAddress, {
      expires: 7,
    });
    Cookies.set('companyCode', userData?.companyId, {
      expires: 7,
    });
    Cookies.set('branchCode', userData?.branchId, {
      expires: 7,
    });

    Cookies.set('financialYear', userData?.financialId, {
      expires: 7,
    });
    if (localStorage.getItem('otp')) {
      navigate('/otp-verification');
      return;
    }
    if (userData?.branches) {
      Cookies.set('branchList', encryptData(userData.branches), {
        expires: 7,
      });
      setTimeout(() => {
        navigate('/userwise/branch/list/');
      }, 1000);
    } else {
      localStorage.setItem('menuData', encryptData(userData?.menus));
      // localStorage.setItem('userData', encryptData(userData?.sessionData));
      localStorage.setItem('userData', encryptData(userData));
      const redirectUrl = sessionStorage.getItem('redirectUrl');
      // setTimeout(() => {
      //   redirectUrl
      //     ? navigate(redirectUrl, { replace: true })
      //     : navigate('/dashboard/', { replace: true });
      // }, 1500);

      setTimeout(() => {
        if (redirectUrl) {
          navigate(redirectUrl, { replace: true });
        } else {
          navigate('/dashboard/', { replace: true });
          window.location.reload(true);
        }
      }, 1500);
    }
  };

  /**
   * Handles the login process by making an API call.
   *
   * @param {string} url - The URL endpoint for the login.
   * @param {Object} values - The login values.
   * @param {number} values.mobileNumber - The mobile number of the user.
   * @param {string} values.password - The password of the user.
   */

  const handleLogin = async (url, values, dispatch) => {
    // const dispatch = useDispatch();
    try {
      setLoading(true);
      // Set a timeout for the API call (e.g., 5 seconds)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Request timed out')); // Reject promise after timeout
        }, 10000); // Timeout duration in milliseconds (5 seconds)
      });

      // Make the API call and race it against the timeout promise
      const response = await Promise.race([
        auth.signIn(url, values),
        timeoutPromise,
      ]);
      if (response?.status === 200) {
        setSuccessLoading(true);
      }
      if (response?.data?.requireOtp) {
        localStorage.setItem('otp', response?.data?.requireOtp);
        setUsername(response?.data?.username);
        navigate('/otp-verification/');
        return;
      }

      const { token, isShow, ...userData } = response.data;
      dispatch &&
        dispatch(
          addFinancialData({ ...userData, token: token, isActive: true }),
        );

      localStorage.setItem('_user_', encodeBase64(JSON.stringify(userData)));
      localStorage.setItem('loginData', encryptData(userData));
      // check if url is admin url then redirect to admin dashboard else redirect to user :)

      if (url === 'admin/login/') {
        Cookies.set('admin_token', encryptData(token), {
          expires: 7,
        });
        Cookies.set('isShow', isShow, { expires: 7 });
        Cookies.set('role', 'admin', { expires: 7 });
        localStorage.setItem('testAminData', encryptData(response.data?.menu));
        Cookies.set('__adminMenu__', encryptData(response.data?.menu), {
          expires: 7,
          secure: false,
        });
        Cookies.set('token', encryptData(response?.data?.access), {
          expires: 7,
        });
        Cookies.set('refreshToken', encryptData(response?.data?.refresh));
        showToast('Admin Login successful', 'success');
        setTimeout(() => {
          navigate('/admin/dashboard/', { replace: true });
        }, 1500);
      } else if (url === 'superadmin/login/') {
        Cookies.set('role', 'superadmin', { secure: false, expires: 7 });
        Cookies.set('superadmin_token', encryptData(token));
        Cookies.set('isShow', isShow);
        localStorage.setItem('testAminData', encryptData(response.data?.menu));
        Cookies.set('__adminMenu__', encryptData(response.data?.menu), {
          secure: false,
          expires: 7,
        });
        Cookies.set('__adminMenu__', encryptData(response.data?.menu), {
          secure: false,
          expires: 7,
        });

        Cookies.set('token', encryptData(response?.data?.access), {
          expires: 7,
        });
        Cookies.set('refreshToken', encryptData(response?.data?.refresh));
        showToast('Super Admin Login successful', 'success');
        setTimeout(() => {
          navigate('/superadmin/dashboard/', { replace: true });
        }, 1500);
      } else {
        // const expiresInHours = 2;
        // const expirationDate = new Date();
        // expirationDate.setTime(
        //   expirationDate.getTime() + expiresInHours * 60 * 60 * 1000,
        // );

        // setTimeout(
        //   () => {
        //     Cookies.remove('token');
        //     Cookies.remove('primaryCalculationType');
        //     Cookies.remove('showImage');
        //     Cookies.remove('primaryWeight');
        //     Cookies.remove('role');
        //     Cookies.remove('companyList');
        //     window.location.href = '/login'; // Redirect to login page
        //   },
        //   20 * 60 * 1000,
        // );

        const expirationDate = new Date(Date.now() + 20 * 60 * 1000);

        Cookies.set('token', encryptData(response?.data?.accessToken), {
          expires: 7,
        });
        Cookies.set('refreshToken', encryptData(response?.data?.refreshToken));
        Cookies.set(
          'primaryCalculationType',
          userData?.primaryCalculationType,
          {
            expires: 7,
          },
        );
        Cookies.set('showImage', userData?.showImage, {
          expires: 7,
        });
        Cookies.set('primaryWeight', userData?.primaryWeight, {
          expires: 7,
        });
        Cookies.set('role', 'user', { secure: false, expires: 7 });
        if (userData?.companies) {
          Cookies.set('companyList', encryptData(userData?.companies), {
            secure: false,
            expires: 7,
          });
        }

        if (userData?.isSingleCompany === true) {
          detailsForSingleBranchAndCompany(userData);
        } else {
          setTimeout(() => {
            navigate('/userwise/company/list/', { replace: true });
          }, 1500);
        }
        showToast('User Login successful', 'success');
      }
    } catch (error) {
      setSuccessLoading(false);
      if (!error.response) {
        showToast(error.message, 'error');
        return;
      }
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        showToast('The request timed out. Please try again later.');
      } else if (!navigator.onLine) {
        showToast(
          'No Internet Connection. Please check your internet connection',
          'error',
        );
      }
      if (error?.message === error?.response?.data?.response) {
        showToast(error.response.data.response, 'error');
      } else {
        const errMessage =
          error?.response?.data.error?.[0] ?? error?.response?.data?.response;
        showToast(errMessage, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        loading,
        successLoading,
        handleLogin,
        menuItems,
        setMenuItems,
        username,
        setUsername,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};
